import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const TeamMemberOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [vendorInfo, setVendorInfo] = useState<{ name?: string; logoUrl?: string; firstName?: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setError(t('auth.teamMember.noInvitationToken'));
      setIsVerifying(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
      const response = await fetch(`${baseUrl}/team-members/verify-magic-link?token=${token}`);
      const data = await response.json();

      if (data.valid) {
        setIsValid(true);
        setVendorInfo({
          name: data.vendorName || data.companyName,
          logoUrl: data.vendorLogoUrl || data.vendorLogo,
          firstName: data.firstName,
        });
      } else {
        setError(data.message || t('auth.teamMember.invalidOrExpired'));
      }
    } catch (err: any) {
      console.error('Token verification error:', err);
      setError(t('auth.teamMember.failedToVerify'));
    } finally {
      setIsVerifying(false);
    }
  };

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];

    if (pwd.length < 8) {
      errors.push(t('auth.teamMember.validationMin8'));
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push(t('auth.teamMember.validationUppercase'));
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push(t('auth.teamMember.validationLowercase'));
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push(t('auth.teamMember.validationNumber'));
    }

    return errors;
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      const errors = validatePassword(value);
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('auth.teamMember.passwordsDoNotMatch'));
      return;
    }

    // Validate password strength
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setError(errors[0]);
      return;
    }

    setIsSettingPassword(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
      const response = await fetch(`${baseUrl}/team-members/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('auth.teamMember.failedToSetPassword'));
      }

      // Success - show success message and redirect
      alert(`✓ ${t('auth.teamMember.successAlert')}`);
      navigate('/login');
    } catch (err: any) {
      console.error('Set password error:', err);
      setError(err.message || t('auth.teamMember.failedToSetPasswordRetry'));
    } finally {
      setIsSettingPassword(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#4339A0] to-[#6B5DD3] p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-16 h-16 animate-spin text-[#4339A0]" />
            <h2 className="text-xl font-semibold text-gray-900">{t('auth.teamMember.verifyingInvitation')}</h2>
            <p className="text-sm text-gray-600 text-center">{t('auth.teamMember.verifyingInvitationMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#4339A0] to-[#6B5DD3] p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('auth.teamMember.invalidInvitation')}</h2>
            <p className="text-gray-600 mb-6">
              {error || t('auth.teamMember.invalidInvitationMessage')}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth.teamMember.contactAdmin')}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#362d85] transition-colors font-medium"
            >
              {t('auth.teamMember.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Valid token - show password setup form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#4339A0] to-[#6B5DD3] p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Logos header */}
        <div className="flex items-center justify-between mb-6 pb-5 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto" />
            <div className="text-left">
              <p className="text-xs font-semibold text-gray-800 leading-tight">Clareefai</p>
              <p className="text-xs text-gray-500 leading-tight">{t('auth.teamMember.customerVoicePlatform')}</p>
            </div>
          </div>
          {vendorInfo?.name && (
            <div className="flex items-center space-x-2">
              {vendorInfo.logoUrl ? (
                <>
                  <img
                    src={vendorInfo.logoUrl}
                    alt={vendorInfo.name}
                    className="h-8 w-auto max-w-[80px] object-contain"
                  />
                  <div className="text-right hidden sm:block">
                    <p className="text-xs font-semibold text-gray-800 leading-tight max-w-[120px] truncate">
                      {vendorInfo.name}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4339A0] to-[#6B5DD3] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <span className="text-white text-xs font-bold tracking-wide">
                      {getInitials(vendorInfo.name)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-800 leading-tight max-w-[120px] truncate">
                      {vendorInfo.name}
                    </p>
                    <p className="text-[10px] text-gray-400 leading-tight">{t('auth.teamMember.company')}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {vendorInfo?.firstName
              ? t('auth.teamMember.welcomeWithName', { name: vendorInfo.firstName })
              : t('auth.teamMember.welcomeToTeam')}
          </h2>
          <p className="text-gray-600">
            {vendorInfo?.name ? t('auth.teamMember.invitedByVendor', { vendor: vendorInfo.name }) : t('auth.teamMember.setupPassword')}
          </p>
        </div>

        <form onSubmit={handleSetPassword} className="space-y-5">
          {/* Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4" />
                <span>{t('auth.teamMember.newPassword')}</span>
              </div>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder={t('auth.teamMember.enterPassword')}
                required
                disabled={isSettingPassword}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-all ${
                  isSettingPassword ? 'bg-gray-100 cursor-not-allowed' : ''
                } ${validationErrors.length > 0 && password ? 'border-amber-400' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Password requirements */}
            {password && validationErrors.length > 0 && (
              <div className="mt-2 space-y-1">
                {validationErrors.map((err, idx) => (
                  <p key={idx} className="text-xs text-amber-600 flex items-center space-x-1">
                    <span className="w-1 h-1 bg-amber-600 rounded-full"></span>
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}
            
            {password && validationErrors.length === 0 && (
              <p className="mt-2 text-xs text-green-600 flex items-center space-x-1">
                <CheckCircle className="w-4 h-4" />
                <span>{t('auth.teamMember.meetsRequirements')}</span>
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.teamMember.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.teamMember.confirmPasswordPlaceholder')}
                required
                disabled={isSettingPassword}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-all ${
                  isSettingPassword ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {confirmPassword && password !== confirmPassword && (
              <p className="mt-2 text-xs text-red-600">{t('auth.teamMember.passwordsDoNotMatch')}</p>
            )}

            {confirmPassword && password === confirmPassword && (
              <p className="mt-2 text-xs text-green-600 flex items-center space-x-1">
                <CheckCircle className="w-4 h-4" />
                <span>{t('auth.teamMember.passwordsMatch')}</span>
              </p>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Password requirements info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">{t('auth.teamMember.passwordRequirements')}</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• {t('auth.teamMember.req8chars')}</li>
              <li>• {t('auth.teamMember.reqCase')}</li>
              <li>• {t('auth.teamMember.reqNumber')}</li>
            </ul>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSettingPassword || validationErrors.length > 0 || !password || !confirmPassword || password !== confirmPassword}
            className={`w-full px-6 py-3 bg-[#4339A0] text-white rounded-lg font-medium transition-all ${
              isSettingPassword || validationErrors.length > 0 || !password || !confirmPassword || password !== confirmPassword
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#362d85] hover:shadow-lg'
            }`}
          >
            {isSettingPassword ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('auth.teamMember.settingPassword')}</span>
              </span>
            ) : (
              t('auth.teamMember.setPasswordAndContinue')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TeamMemberOnboarding;
